/**
 * Pure utility functions for peripheral association/dissociation.
 */

import {
    createDefaultKitchenTicket,
    createDefaultCustomerTicket,
    createDefaultModelKitchenTicket,
    createDefaultGeneralSettings,
    createDefaultDiversSettings,
    isCustomerTicket,
    isKitchenTicket,
} from "@helpers/printerDefaults";

/**
 * Checks which items from lastList are not present in newList (i.e., were removed/dissociated).
 */
export function checkDisassociatedPeripheral(
    lastList: any[],
    newList: any[]
): any[] {
    const res: any[] = [];
    for (let index = 0; index < lastList.length; index++) {
        const e = lastList[index];
        if (newList.indexOf(e) === -1 && e !== undefined) {
            res.push(e);
        }
    }
    return res;
}

/**
 * Gets an array of non-empty application rows from selectedRows.
 */
export function getArrayOfRows(
    selectedRows: any[],
    applicationLabel: string
): any[] {
    let arrayOfRows: any[] = [];
    selectedRows.forEach((elt: any) => {
        if (elt[applicationLabel] !== "") {
            arrayOfRows.push(elt);
        }
    });
    return [...new Set(arrayOfRows)];
}

/**
 * Builds a printer object for association.
 * When isFromSelectedRows is true, builds default ticket/general/divers config using salesModes.
 * When false, preserves existing config from the element.
 */
export function buildPrinterObject(
    el: any,
    isFromSelectedRows: boolean,
    salesModes: any[],
    t: (key: string) => string
): any {
    if (isFromSelectedRows) {
        const ticketValue = el["tickets"]?.value;
        const isCustomerActive = isCustomerTicket(ticketValue);
        const isKitchenActive = isKitchenTicket(ticketValue);
        // Preserve the printer's own saved configuration when it already has
        // one (printers come from /settings/printers with these blocks); only
        // fall back to defaults for a brand-new printer with no config yet.
        // Otherwise associating a configured printer to several applications
        // would silently reset its ticket/general/divers settings.
        return {
            id_printer: el["Id"],
            designation: el[t("Printer name")],
            mark: el[t("Mark")],
            port: el["port"],
            note: el[t("Remark")],
            status: "in line",
            state: "to configure",
            path: el["path"],
            font_size: el[t("Font size")],
            edit_date: "",
            type: el["type"],
            add_date: "",
            speed: el["speed"],
            ip: el["ip"],
            port_series: el["port_series"],
            ticket_width: el["ticket_width"],
            tickets: el["tickets"],
            code: el["code"],
            general: el["general"] ?? createDefaultGeneralSettings(true),
            customer_ticket:
                el["customer_ticket"] ??
                createDefaultCustomerTicket(salesModes, isCustomerActive),
            Kitchen_ticket:
                el["Kitchen_ticket"] ??
                createDefaultKitchenTicket(salesModes, isKitchenActive),
            modelKitchenTicket:
                el["modelKitchenTicket"] ?? createDefaultModelKitchenTicket(),
            divers: el["divers"] ?? createDefaultDiversSettings(),
            isArchived:
                el["isArchived"] !== undefined ? el["isArchived"] : false,
        };
    } else {
        return {
            id_printer: el["Id"],
            designation:
                el.designation !== undefined
                    ? el.designation
                    : el[t("Printer name")],
            mark: el[t("Mark")],
            port: el["port"],
            note: el[t("Remark")],
            status: "in line",
            state: "to configure",
            path: el["path"],
            font_size: el[t("Font size")],
            edit_date: "",
            type: el["type"],
            add_date: "",
            speed: el["speed"],
            ip: el["ip"],
            port_series: el["port_series"],
            ticket_width: el["ticket_width"],
            tickets: el["tickets"],
            code: el["code"],
            general: el["general"],
            customer_ticket: el["customer_ticket"],
            Kitchen_ticket: el["Kitchen_ticket"],
            modelKitchenTicket: el["modelKitchenTicket"],
            divers: el["divers"],
            isArchived:
                el["isArchived"] !== undefined ? el["isArchived"] : false,
        };
    }
}

/**
 * Builds a monetic object for association.
 */
export function buildMoneticObject(
    el: any,
    t: (key: string) => string
): any {
    return {
        id_monetic: el["Id"],
        designation: el[t("Designation")],
        mark: el[t("Mark")],
        port: el["port"],
        note: el[t("Remark")],
        status: "in line",
        state: "to configure",
        path: el["url"],
        android: el["android"],
        merchant_code: el["merchant_code"],
        tpe_name: el["tpe_name"],
        device_code: el["device_code"],
        speed: el["speed"],
        ip: el["ip"],
        payment_type: el["payment_type"],
        kiss: el["kiss"],
        reference: el[t("Reference")],
        general: {},
        terminal_type: el["terminal_type"],
        setup_type: el["setup_type"],
        timeout: el["timeout"],
        payment_method: el["payment_method"],
        webhook: el.webhook,
        adyenIp: el.adyenIpAddress ?? el.adyenIp,
        mode: el.mode,
        idIntegrator: el.idIntegrator,
        autoValidate: el.autoValidate,
        currency: el.currency,
        adyenConfig: el.adyenConfig,
        key: el.key,
        serialNumber: el.serialNumber,
        communication: el.communication,
    };
}

/**
 * Builds a cash management object for association.
 */
export function buildCashManagementObject(
    el: any,
    t: (key: string) => string
): any {
    return {
        id_cash_management: el["Id"],
        designation: el[t("Designation")],
        mark: el[t("Mark")],
        port_number: el["port_number"],
        reference: el[t("Reference")],
        note: el[t("Remark")],
        server_directory: el["server_directory"],
        activate: "",
        user: el["user"],
        ip: el["ip"],
        password: el["password"],
        pos_id: el["pos_id"],
        coin_loading_alert: {},
        empty_coin_alert: {},
        coin_status: {},
        collection_alert: {},
        withdrawal_Reason: {},
        maintenance: {},
        cash_Fund: {},
        accepted_denomination: {
            "5.00 €": false,
            "10.00 €": false,
            "20.00 €": false,
            "50.00 €": false,
            "100.00 €": false,
            "200.00 €": false,
            "500.00 €": false,
        },
    };
}
